import { useParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { format } from 'date-fns';
import {
  ArrowLeft,
  Shield,
  Clock,
  Hash,
  FileText,
  Users,
  Lock,
} from 'lucide-react';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import { Skeleton } from '@/components/ui/skeleton';
import { getLocalizedName } from '@/lib/utils';
import { useAuthStore } from '@/features/auth/stores/auth.store';
import { formatRelativeTime } from '@/lib/utils/date';
import { useRole } from '../hooks';
import { RoleStatusBadge, RoleTypeBadge, CreateRoleDialog } from '../components';
import type { Permission } from '../types';

function InfoField({
  icon: Icon,
  label,
  value,
}: {
  icon?: React.ComponentType<{ className?: string }>;
  label: string;
  value: string | React.ReactNode;
}) {
  return (
    <div className="flex items-start gap-3">
      {Icon && <Icon className="h-4 w-4 text-slate-400 mt-0.5 shrink-0" />}
      <div className="min-w-0 flex-1">
        <p className="text-xs font-medium uppercase tracking-wider text-slate-500 mb-1">
          {label}
        </p>
        <p className="text-sm font-medium text-slate-900">{value || '-'}</p>
      </div>
    </div>
  );
}

function groupPermissions(permissions: Permission[]): Record<string, Permission[]> {
  const groups: Record<string, Permission[]> = {};
  for (const perm of permissions) {
    const [resource] = perm.name.split(':');
    const key = resource || 'other';
    if (!groups[key]) groups[key] = [];
    groups[key].push(perm);
  }
  return groups;
}

export function RoleDetailPage() {
  const { id } = useParams<{ id: string }>();
  const { t, i18n } = useTranslation();
  const hasAccess = useAuthStore((state) => state.hasAccess);
  const { data: role, isLoading, isError, error } = useRole(id!);

  if (isLoading) {
    return (
      <div className="min-h-screen bg-slate-50">
        <div className="bg-white border-b border-slate-200 px-6 py-8">
          <div className="w-full">
            <Skeleton className="h-4 w-24 mb-6" />
            <div className="flex items-center gap-6">
              <Skeleton className="h-16 w-16 rounded-full" />
              <div className="space-y-3">
                <Skeleton className="h-10 w-48" />
                <div className="flex gap-2">
                  <Skeleton className="h-6 w-24" />
                  <Skeleton className="h-6 w-20" />
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="w-full px-6 py-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <Skeleton className="h-48" />
            <Skeleton className="h-48" />
            <Skeleton className="h-48" />
          </div>
        </div>
      </div>
    );
  }

  if (isError || !role) {
    return (
      <div className="min-h-screen bg-slate-50 p-6">
        <div className="w-full">
          <div className="bg-white rounded-lg border border-slate-200 p-8 text-center">
            <div className="text-red-500 mb-4">
              <Shield className="h-12 w-12 mx-auto mb-2 opacity-50" />
              <p className="text-lg font-medium">{error?.message || t('roleNotFound')}</p>
            </div>
            <Link to="/app/roles">
              <Button variant="outline">
                <ArrowLeft className="mr-2 h-4 w-4" />
                {t('backToList')}
              </Button>
            </Link>
          </div>
        </div>
      </div>
    );
  }

  const statusColors =
    role.status === 'ACTIVE'
      ? { bg: 'bg-green-100', icon: 'text-green-600' }
      : { bg: 'bg-slate-100', icon: 'text-slate-600' };

  const permissionGroups = groupPermissions(role.permissions ?? []);
  const groupKeys = Object.keys(permissionGroups).sort();

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Hero Header */}
      <div className="bg-white border-b border-slate-200">
        <div className="w-full px-6 py-6">
          <Link
            to="/app/roles"
            className="inline-flex items-center text-sm font-medium text-slate-600 hover:text-slate-900 mb-6 transition-colors"
          >
            <ArrowLeft className="mr-2 h-4 w-4" />
            {t('backToList')}
          </Link>

          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6">
            <div className="flex items-center gap-5">
              <div
                className={`h-16 w-16 rounded-full flex items-center justify-center border-4 border-white shadow-lg ${statusColors.bg}`}
              >
                <Shield className={`h-8 w-8 ${statusColors.icon}`} />
              </div>

              <div>
                <h1 className="text-2xl sm:text-3xl font-bold text-slate-900 mb-2">
                  {getLocalizedName(role, i18n.language)}
                </h1>
                <div className="flex flex-wrap items-center gap-2">
                  <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-slate-100 border border-slate-300 text-xs font-medium uppercase tracking-wide text-slate-700">
                    <Hash className="h-3.5 w-3.5" />
                    {t('role')} #{String(role.id).padStart(5, '0')}
                  </span>
                  <RoleStatusBadge status={role.status} />
                  <RoleTypeBadge type={role.type} />
                </div>
              </div>
            </div>

            <div className="flex flex-wrap gap-2">
              {hasAccess('role', 'update') && <CreateRoleDialog id={role.id} />}
            </div>
          </div>
        </div>
      </div>

      {/* Stats Bar */}
      <div className="bg-slate-50 border-b border-slate-200">
        <div className="w-full px-6 py-4">
          <div className="flex flex-wrap items-center justify-center sm:justify-start gap-6 sm:gap-8">
            <div className="text-center sm:text-left">
              <p className="text-xs font-medium uppercase tracking-wider text-slate-500 mb-1">
                {t('type')}
              </p>
              <p className="text-xl font-bold text-slate-900">{t(role.type.toLowerCase())}</p>
            </div>
            <Separator orientation="vertical" className="h-10 hidden sm:block" />
            <div className="text-center sm:text-left">
              <p className="text-xs font-medium uppercase tracking-wider text-slate-500 mb-1">
                {t('status.status')}
              </p>
              <p className={`text-xl font-bold ${role.status === 'ACTIVE' ? 'text-green-600' : 'text-slate-500'}`}>
                {t(`status.${role.status}`)}
              </p>
            </div>
            <Separator orientation="vertical" className="h-10 hidden sm:block" />
            <div className="text-center sm:text-left">
              <p className="text-xs font-medium uppercase tracking-wider text-slate-500 mb-1">
                {t('permissions')}
              </p>
              <p className="text-xl font-bold text-slate-900">{role.permissions?.length ?? 0}</p>
            </div>
            <Separator orientation="vertical" className="h-10 hidden sm:block" />
            <div className="text-center sm:text-left">
              <p className="text-xs font-medium uppercase tracking-wider text-slate-500 mb-1">
                {t('createdAt')}
              </p>
              <p className="text-xl font-bold text-slate-900">
                {role.createdAt ? format(new Date(role.createdAt), 'MMM d, yyyy') : '-'}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Info Cards */}
      <div className="w-full px-6 py-6">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {/* Basic Info Card */}
          <Card className="bg-white border-slate-200 shadow-sm hover:shadow-md transition-shadow">
            <CardHeader className="pb-3 border-b border-slate-100">
              <CardTitle className="text-lg font-semibold text-slate-900 flex items-center gap-2">
                <FileText className="h-5 w-5 text-slate-400" />
                {t('basicInfo')}
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-4 space-y-4">
              <InfoField icon={Shield} label={t('name')} value={role.name} />
              <InfoField icon={Shield} label={t('nameEn')} value={role.name_en || '-'} />
              <InfoField icon={Shield} label={t('nameRu')} value={role.name_ru || '-'} />
              <InfoField
                icon={Users}
                label={t('type')}
                value={<RoleTypeBadge type={role.type} />}
              />
              <InfoField
                label={t('status.status')}
                value={<RoleStatusBadge status={role.status} />}
              />
              <InfoField
                icon={FileText}
                label={t('description')}
                value={role.description || '-'}
              />
            </CardContent>
          </Card>

          {/* System Info Card */}
          <Card className="bg-white border-slate-200 shadow-sm hover:shadow-md transition-shadow">
            <CardHeader className="pb-3 border-b border-slate-100">
              <CardTitle className="text-lg font-semibold text-slate-900 flex items-center gap-2">
                <Clock className="h-5 w-5 text-slate-400" />
                {t('systemInfo')}
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-4 space-y-4">
              <InfoField label={t('id')} value={String(role.id)} />
              <InfoField
                icon={Clock}
                label={t('createdAt')}
                value={
                  role.createdAt
                    ? format(new Date(role.createdAt), 'MMM d, yyyy HH:mm')
                    : '-'
                }
              />
              <InfoField
                icon={Clock}
                label={t('updatedAt')}
                value={
                  role.updatedAt ? (
                    <span>
                      {format(new Date(role.updatedAt), 'MMM d, yyyy HH:mm')}
                      <span className="text-slate-400 text-xs ml-2">
                        ({formatRelativeTime(role.updatedAt)})
                      </span>
                    </span>
                  ) : (
                    '-'
                  )
                }
              />
            </CardContent>
          </Card>

          {/* Permissions Details Card */}
          <Card className="bg-white border-slate-200 shadow-sm hover:shadow-md transition-shadow md:col-span-2 lg:col-span-3">
            <CardHeader className="pb-3 border-b border-slate-100">
              <CardTitle className="text-lg font-semibold text-slate-900 flex items-center gap-2">
                <Lock className="h-5 w-5 text-slate-400" />
                {t('permissions')}
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-4">
              {groupKeys.length > 0 ? (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  {groupKeys.map((group) => (
                    <div key={group} className="rounded-lg border border-slate-200 p-4">
                      <h4 className="text-sm font-semibold text-slate-800 capitalize mb-3 flex items-center gap-2">
                        <Shield className="h-4 w-4 text-slate-400" />
                        {group}
                      </h4>
                      <div className="flex flex-wrap gap-1.5">
                        {permissionGroups[group].map((perm) => {
                          const action = perm.name.split(':')[1] || perm.name;
                          return (
                            <Badge
                              key={perm.id}
                              variant="outline"
                              className="text-xs font-medium"
                            >
                              {action}
                            </Badge>
                          );
                        })}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-slate-500">{t('noPermissions')}</p>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
